import { useState, useEffect, useRef } from 'react';
import { useIdeas } from '@/hooks/useIdeas';
import { PageShell, Section } from '@/components/layout';
import { IdeaInput } from '@/components/IdeaInput';
import { IdeaCard } from '@/components/IdeaCard';
import { IdeaDetailSheet } from '@/components/IdeaDetailSheet';
import { WaterSheet } from '@/components/WaterSheet';
import { CoFounderSentence } from '@/components/CoFounderSentence';
import { Skeleton } from '@/components/ui/skeleton';
import type { Idea, Mood } from '@/lib/types';

export default function Garden() {
  const {
    ideas,
    loading,
    plantSeed,
    waterIdea,
    updateIdea,
    archiveIdea,
    deleteIdea,
    setFocus,
    clearFocus,
  } = useIdeas();
  const [selectedIdea, setSelectedIdea] = useState<Idea | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [waterOpen, setWaterOpen] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Listen for focus input event from sidebar
  useEffect(() => {
    const handleFocusInput = () => {
      inputRef.current?.focus();
    };
    window.addEventListener('yelv-focus-input', handleFocusInput);
    return () => window.removeEventListener('yelv-focus-input', handleFocusInput);
  }, []);

  // Check if there's a focus idea among active ideas
  const hasFocusIdea = ideas.some(i => i.is_focus && i.status === 'active');

  // Filter and sort ideas - Focus idea pinned at top, then sorted by last_watered_at ascending
  const activeIdeas = ideas
    .filter(i => i.status === 'active' && i.growth_stage !== 'tree')
    .sort((a, b) => {
      // Focus idea always first
      if (a.is_focus && !b.is_focus) return -1;
      if (!a.is_focus && b.is_focus) return 1;
      // Then by oldest first
      const aDate = a.last_watered_at || a.created_at;
      const bDate = b.last_watered_at || b.created_at;
      return new Date(aDate).getTime() - new Date(bDate).getTime();
    });

  const handleCardClick = (idea: Idea) => {
    setSelectedIdea(idea);
    setDetailOpen(true);
  };

  const handleWater = () => {
    setDetailOpen(false);
    setWaterOpen(true);
  };

  const handleWaterSubmit = async (reflection: string, nextStep?: string, mood?: Mood) => {
    if (!selectedIdea) return false;
    return waterIdea(selectedIdea.id, reflection, nextStep, mood);
  };

  return (
    <PageShell narrow>
      {/* Capture Zone - Dominant Focus */}
      <section className="py-8 md:py-12">
        <div className="text-center mb-10 space-y-4">
          <h1 className="text-2xl md:text-3xl font-medium text-foreground">
            Hold it for a moment.
          </h1>
          <p className="text-base text-muted-foreground/70 font-light">
            You can return to it later.
          </p>
        </div>
        <IdeaInput 
          ref={inputRef}
          onSubmit={async text => {
            await plantSeed(text);
          }} 
        />
      </section>

      {/* Soft Divider */}
      <div className="max-w-md mx-auto mb-8">
        <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      </div>

      {/* Co-Founder Sentence - Truth Line */}
      {!loading && <CoFounderSentence ideas={ideas} />}

      {/* Living Garden */}
      <Section>
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}
          </div>
        ) : activeIdeas.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-sm text-muted-foreground/50">
              No seeds planted yet.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground/60 mb-4">
              Your ideas, waiting.
            </p>
            {activeIdeas.map(idea => (
              <IdeaCard 
                key={idea.id} 
                idea={idea} 
                onClick={() => handleCardClick(idea)}
                showFocusMuting={hasFocusIdea}
              />
            ))}
          </div>
        )}
      </Section>

      {/* Detail Sheet */}
      <IdeaDetailSheet 
        idea={selectedIdea} 
        open={detailOpen} 
        onOpenChange={setDetailOpen} 
        onUpdate={updateIdea} 
        onWater={handleWater} 
        onArchive={archiveIdea} 
        onDelete={deleteIdea}
        onSetFocus={setFocus}
        onClearFocus={clearFocus}
      />

      {/* Water Sheet */}
      <WaterSheet 
        open={waterOpen} 
        onOpenChange={setWaterOpen} 
        onSubmit={handleWaterSubmit} 
        ideaTitle={selectedIdea?.title || undefined} 
        isTree={selectedIdea?.growth_stage === 'tree'} 
      />
    </PageShell>
  );
}
